import { useCallback, useEffect, useRef, useState } from "react";
import { CsrAPI } from "../apis/CsrAPI";
import {
  CHAT_MAX_POLL_MS,
  CHAT_MESSAGES,
  CHAT_MESSAGE_ROLE,
  CHAT_MESSAGE_STATUS,
  CHAT_POLL_INTERVAL_MS,
  SESSION_TITLE_MAX_LENGTH,
} from "../utils/triloChatConstants";

const toUiMessage = m => ({
  role: m.role,
  content:
    typeof m.content === "string" ? m.content : JSON.stringify(m.content),
  options: m.options || [],
  drafts: m.drafts || [],
});

/**
 * All conversation state for MW: the session list, the open session and its
 * messages, the study context, and sending a message.
 *
 * A message is processed by a background Lambda, so sending POSTs the message
 * and then polls for the reply (see CHAT_POLL_INTERVAL_MS / CHAT_MAX_POLL_MS).
 *
 * Every request is gated on the MW permissions the caller passes in: without a
 * permission the request is never sent rather than being sent and answered
 * with a 403. The flags default to true so a caller that does not gate (tests,
 * and any future non-RBAC usage) keeps the previous behaviour.
 *
 * @param {Object} options
 * @param {Function} [options.onNewSession] - Called after a session is created,
 *   so the page can move focus to the composer.
 * @param {boolean} [options.canRead] - mw:sessions:read: load the session list
 *   and open a session.
 * @param {boolean} [options.canCreate] - mw:sessions:create: start a session.
 * @param {boolean} [options.canUpdate] - mw:sessions:update: change the study
 *   a session is bound to.
 * @param {boolean} [options.canSendMessage] - mw:sessions:update plus
 *   mw:messages:create: post a message to a session.
 * @param {boolean} [options.canDelete] - mw:sessions:delete: delete a session.
 * @returns {Object} chat state and the actions that change it
 */
export const useTriloChatSessions = ({
  onNewSession,
  canRead = true,
  canCreate = true,
  canUpdate = true,
  canSendMessage = true,
  canDelete = true,
} = {}) => {
  const [sessions, setSessions] = useState([]);
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [activeSession, setActiveSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [activeToolCalls, setActiveToolCalls] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(true);
  const [studies, setStudies] = useState([]);
  const [selectedStudyId, setSelectedStudyId] = useState("");

  const pollIntervalRef = useRef(null);

  const stopPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  }, []);

  useEffect(() => stopPolling, [stopPolling]);

  // Load sessions and studies on mount
  useEffect(() => {
    CsrAPI.getAllStudies()
      .then(docs => setStudies((docs || []).filter(d => d.id && d.name)))
      .catch(() => {});

    // Without mw:sessions:read the list is never fetched; the sidebar shows
    // its empty state instead of a list the API would refuse to serve.
    if (!canRead) {
      setSessions([]);
      setLoadingSessions(false);
      return;
    }

    CsrAPI.listChatSessions()
      .then(data => {
        const sorted = (data.sessions || []).sort(
          (a, b) => new Date(b.updated_at) - new Date(a.updated_at)
        );
        setSessions(sorted);
      })
      .catch(() => {})
      .finally(() => setLoadingSessions(false));
  }, [canRead]);

  const selectSession = useCallback(
    sessionId => {
      if (!canRead) return;
      setActiveSessionId(sessionId);
      setActiveToolCalls([]);
      CsrAPI.getChatSession({ sessionId }).then(data => {
        setActiveSession(data);
        setSelectedStudyId(data.study_id || "");
        setMessages((data.messages || []).map(toUiMessage));
      });
    },
    [canRead]
  );

  const handleStudyChange = useCallback(
    async studyId => {
      setSelectedStudyId(studyId);
      // Re-pointing a session at another study is a session update, so it
      // follows mw:sessions:update; the picker still moves locally.
      if (activeSessionId && canUpdate) {
        try {
          const updated = await CsrAPI.updateChatSession({
            sessionId: activeSessionId,
            studyId,
          });
          setActiveSession(prev => ({ ...prev, study_id: updated.study_id }));
        } catch {
          // silent — dropdown already updated locally
        }
      }
    },
    [activeSessionId, canUpdate]
  );

  const createNewSession = useCallback(
    async studyId => {
      if (!canCreate) return;
      try {
        const session = await CsrAPI.createChatSession({
          studyId: studyId || null,
        });
        setSessions(prev => [session, ...prev]);
        setActiveSessionId(session.session_id);
        setActiveSession(session);
        setSelectedStudyId("");
        setMessages([]);
        setActiveToolCalls([]);
        onNewSession?.();
      } catch {
        // session creation failed silently; user can retry
      }
    },
    [canCreate, onNewSession]
  );

  const deleteSession = useCallback(
    async (e, sessionId) => {
      e.stopPropagation();
      if (!canDelete) return;
      try {
        await CsrAPI.deleteChatSession({ sessionId });
        setSessions(prev => prev.filter(s => s.session_id !== sessionId));
        if (activeSessionId === sessionId) {
          setActiveSessionId(null);
          setActiveSession(null);
          setMessages([]);
          setActiveToolCalls([]);
        }
      } catch {
        // deletion failed silently
      }
    },
    [activeSessionId, canDelete]
  );

  const failWith = useCallback(content => {
    setMessages(prev => [...prev, { role: CHAT_MESSAGE_ROLE.error, content }]);
    setIsStreaming(false);
    setActiveToolCalls([]);
  }, []);

  const sendMessage = useCallback(
    async rawText => {
      const text = (rawText || "").trim();
      if (!text || isStreaming || !canSendMessage) return;

      let sessionId = activeSessionId;

      // Auto-create a session if none is selected
      if (!sessionId) {
        if (!canCreate) return;
        try {
          const session = await CsrAPI.createChatSession({
            studyId: selectedStudyId || null,
          });
          setSessions(prev => [session, ...prev]);
          setActiveSessionId(session.session_id);
          setActiveSession(session);
          sessionId = session.session_id;
        } catch {
          return;
        }
      }

      setMessages(prev => [
        ...prev,
        { role: CHAT_MESSAGE_ROLE.user, content: text },
      ]);
      setIsStreaming(true);
      setActiveToolCalls([]);

      try {
        await CsrAPI.sendChatMessage({ sessionId, message: text });

        // Poll for the result — a background Lambda processes the message
        // asynchronously.
        const pollStart = Date.now();

        pollIntervalRef.current = setInterval(async () => {
          // Guard: if already handled (ref nulled), bail — multiple in-flight
          // ticks can all see "complete"
          if (!pollIntervalRef.current) return;

          if (Date.now() - pollStart > CHAT_MAX_POLL_MS) {
            stopPolling();
            failWith(CHAT_MESSAGES.timeoutError);
            return;
          }

          try {
            const data = await CsrAPI.getChatMessageStatus({ sessionId });

            if (data.status === CHAT_MESSAGE_STATUS.complete) {
              if (!pollIntervalRef.current) return; // another tick handled it
              stopPolling();
              const msgs = data.messages || [];
              const last = msgs[msgs.length - 1];
              if (
                last &&
                last.role === CHAT_MESSAGE_ROLE.assistant &&
                last.content
              ) {
                setMessages(prev => [
                  ...prev,
                  {
                    role: CHAT_MESSAGE_ROLE.assistant,
                    content: last.content,
                    options: last.options || [],
                    drafts: last.drafts || [],
                  },
                ]);
                setSessions(prev =>
                  prev.map(s =>
                    s.session_id === sessionId
                      ? {
                          ...s,
                          title:
                            s.title || text.slice(0, SESSION_TITLE_MAX_LENGTH),
                          updated_at: new Date().toISOString(),
                        }
                      : s
                  )
                );
              }
              setIsStreaming(false);
              setActiveToolCalls([]);
            } else if (data.status === CHAT_MESSAGE_STATUS.error) {
              if (!pollIntervalRef.current) return;
              stopPolling();
              failWith(data.message || CHAT_MESSAGES.genericError);
            }
            // "pending" or "idle" → keep polling
          } catch {
            // transient network error — keep polling
          }
        }, CHAT_POLL_INTERVAL_MS);
      } catch {
        failWith(CHAT_MESSAGES.genericError);
      }
    },
    [
      isStreaming,
      canSendMessage,
      canCreate,
      activeSessionId,
      selectedStudyId,
      stopPolling,
      failWith,
    ]
  );

  // Quick-reply buttons offered by the last assistant message, once it has
  // finished arriving.
  const lastMessage =
    !isStreaming && messages.length > 0 ? messages[messages.length - 1] : null;
  const quickReplies =
    lastMessage?.role === CHAT_MESSAGE_ROLE.assistant
      ? lastMessage.options || []
      : [];

  return {
    sessions,
    activeSessionId,
    activeSession,
    messages,
    isStreaming,
    activeToolCalls,
    loadingSessions,
    studies,
    selectedStudyId,
    setSelectedStudyId,
    quickReplies,
    selectSession,
    createNewSession,
    deleteSession,
    handleStudyChange,
    sendMessage,
  };
};
